import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  BarChart3, 
  Shield, 
  Users, 
  Zap, 
  CheckCircle2, 
  ArrowRight,
  Network,
  Brain,
  Lock,
  LineChart,
  Layers,
  Building2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

// MediaKshetra Brand Colors - Enterprise Dark Theme
const GOLD = '#D4A84B';
const GOLD_MUTED = '#B8975F';
const DARK_BG = '#0E0E0E';
const CARD_BG = '#141414';
const MUTED = '#9CA3AF';
const CHARCOAL = '#1A1A1A';

export const LandingPage = () => {
  const navigate = useNavigate();

  const handleRequestAccess = () => {
    navigate('/login');
  };

  const capabilities = [
    {
      icon: Network,
      title: 'Infrastructure Orchestration',
      description: 'Unified control across 42+ advertising platforms with institutional-grade connectivity'
    },
    {
      icon: Brain,
      title: 'AI Agent Architecture',
      description: 'Specialized agents for insights, creative, and media optimization with human validation'
    },
    {
      icon: Shield,
      title: 'Governance & Compliance',
      description: 'Enterprise security, audit trails, and approval workflows for regulated environments'
    },
    {
      icon: LineChart,
      title: 'Executive Intelligence',
      description: 'Cross-platform insights and performance measurement at institutional scale'
    }
  ];

  const enterpriseFeatures = [
    'Unlimited platform connections',
    'Unlimited workspaces',
    'SSO support',
    'Dedicated onboarding',
    'Custom reporting',
    'API access',
    'Governance dashboard',
    'Executive-level insights',
    'Advanced AI agent orchestration',
    'Advisory layer integration'
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: DARK_BG }}>
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b" style={{ backgroundColor: DARK_BG, borderColor: `${GOLD}1A` }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded flex items-center justify-center" style={{ backgroundColor: GOLD }}>
                <BarChart3 className="w-5 h-5 text-black" />
              </div>
              <span className="text-xl font-semibold tracking-tight text-white">MediaKshetra</span>
              <span className="text-xs tracking-widest uppercase" style={{ color: GOLD_MUTED }}>GIOS</span>
            </div>
            <Button 
              onClick={handleRequestAccess}
              data-testid="nav-login-button"
              variant="outline"
              className="text-sm font-medium border-opacity-50 hover:bg-white/5"
              style={{ borderColor: GOLD_MUTED, color: GOLD }}
            >
              Access Portal
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-24 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded mb-8"
            style={{ backgroundColor: `${GOLD}0D`, border: `1px solid ${GOLD}1A` }}
          >
            <Lock className="w-3 h-3" style={{ color: GOLD_MUTED }} />
            <span className="text-xs tracking-wider uppercase" style={{ color: GOLD_MUTED }}>Private Beta Access</span>
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-5xl sm:text-6xl lg:text-7xl font-light text-white mb-4 tracking-tight"
          >
            Govern. Orchestrate. Scale.
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-xl sm:text-2xl font-light mb-8"
            style={{ color: GOLD }}
          >
            The Growth Infrastructure Operating System.
          </motion.p>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-base sm:text-lg mb-12 max-w-3xl mx-auto leading-relaxed"
            style={{ color: MUTED }}
          >
            GIOS connects platforms, teams, and intelligence layers into one structured 
            performance environment — powered by AI, validated by humans.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Button 
              onClick={handleRequestAccess}
              data-testid="hero-request-access-button"
              size="lg"
              className="px-10 h-12 text-sm font-medium tracking-wide uppercase transition-all duration-300"
              style={{ 
                backgroundColor: GOLD, 
                color: '#000',
                letterSpacing: '0.1em'
              }}
            >
              Request Access <ArrowRight className="ml-3 w-4 h-4" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-24 px-4" style={{ backgroundColor: CHARCOAL }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-light text-white mb-4 tracking-tight">
              Infrastructure Capabilities
            </h2>
            <p style={{ color: MUTED }}>Institutional-grade performance management</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {capabilities.map((capability, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                viewport={{ once: true }}
              >
                <Card 
                  className="p-8 h-full transition-all duration-300"
                  style={{ 
                    backgroundColor: CARD_BG, 
                    border: `1px solid ${GOLD}1A`,
                  }}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded flex items-center justify-center flex-shrink-0" 
                         style={{ backgroundColor: `${GOLD}15` }}>
                      <capability.icon className="w-5 h-5" style={{ color: GOLD }} />
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-white mb-2">{capability.title}</h3>
                      <p className="text-sm leading-relaxed" style={{ color: MUTED }}>{capability.description}</p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise Section */}
      <section className="py-24 px-4" style={{ backgroundColor: DARK_BG }}>
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded mb-6"
                   style={{ backgroundColor: `${GOLD}0D`, border: `1px solid ${GOLD}1A` }}>
                <Building2 className="w-3 h-3" style={{ color: GOLD_MUTED }} />
                <span className="text-xs tracking-wider uppercase" style={{ color: GOLD_MUTED }}>Enterprise</span>
              </div>
              
              <h2 className="text-3xl sm:text-4xl font-light text-white mb-6 tracking-tight">
                Structured for Scale
              </h2>
              
              <p className="text-base leading-relaxed mb-8" style={{ color: MUTED }}>
                GIOS is architected for agencies and enterprises requiring institutional-grade 
                governance, cross-platform orchestration, and AI-augmented decision-making 
                with human oversight.
              </p>
              
              <Button 
                onClick={handleRequestAccess}
                data-testid="enterprise-cta-button"
                className="px-8 h-11 text-sm font-medium tracking-wide transition-all duration-300"
                style={{ 
                  backgroundColor: 'transparent', 
                  color: GOLD,
                  border: `1px solid ${GOLD}66`
                }}
              >
                Speak to MediaKshetra
              </Button>
            </div>
            
            <Card 
              className="p-8"
              style={{ backgroundColor: CARD_BG, border: `1px solid ${GOLD}1A` }}
            >
              <h3 className="text-lg font-medium text-white mb-6">Enterprise Capabilities</h3>
              <ul className="space-y-4">
                {enterpriseFeatures.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: GOLD }} />
                    <span className="text-sm" style={{ color: MUTED }}>{feature}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* AI Architecture Section */}
      <section className="py-24 px-4" style={{ backgroundColor: CHARCOAL }}>
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded mb-6"
               style={{ backgroundColor: `${GOLD}0D`, border: `1px solid ${GOLD}1A` }}>
            <Brain className="w-3 h-3" style={{ color: GOLD_MUTED }} />
            <span className="text-xs tracking-wider uppercase" style={{ color: GOLD_MUTED }}>Agentic AI</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl font-light text-white mb-6 tracking-tight">
            Intelligence with Governance
          </h2>
          
          <p className="text-base leading-relaxed max-w-2xl mx-auto mb-12" style={{ color: MUTED }}>
            Specialized AI agents orchestrated through structured workflows with human-in-the-loop 
            validation. Every recommendation is traceable, every action is auditable.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { name: 'Insight Agent', desc: 'Performance analysis, trend detection, opportunity identification' },
              { name: 'Creative Agent', desc: 'Content generation, variant testing, messaging optimization' },
              { name: 'Media Agent', desc: 'Budget orchestration, channel allocation, forecasting' }
            ].map((agent, idx) => (
              <Card 
                key={idx}
                className="p-6"
                style={{ backgroundColor: CARD_BG, border: `1px solid ${GOLD}1A` }}
              >
                <div className="w-10 h-10 rounded mx-auto mb-4 flex items-center justify-center"
                     style={{ backgroundColor: `${GOLD}15` }}>
                  <Layers className="w-5 h-5" style={{ color: GOLD }} />
                </div>
                <h3 className="text-base font-medium text-white mb-2">{agent.name}</h3>
                <p className="text-xs leading-relaxed" style={{ color: MUTED }}>{agent.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4" style={{ backgroundColor: DARK_BG }}>
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-light text-white mb-6 tracking-tight">
            Begin Private Access
          </h2>
          
          <p className="text-base leading-relaxed mb-10" style={{ color: MUTED }}>
            GIOS is currently available through private access for qualified agencies 
            and enterprise teams. Contact MediaKshetra to discuss your requirements.
          </p>
          
          <Button 
            onClick={handleRequestAccess}
            data-testid="cta-request-access-button"
            size="lg"
            className="px-10 h-12 text-sm font-medium tracking-wide uppercase transition-all duration-300"
            style={{ 
              backgroundColor: GOLD, 
              color: '#000',
              letterSpacing: '0.1em'
            }}
          >
            Request Access
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4" style={{ backgroundColor: CHARCOAL, borderTop: `1px solid ${GOLD}0D` }}>
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <div className="w-5 h-5 rounded flex items-center justify-center" style={{ backgroundColor: GOLD }}>
              <BarChart3 className="w-3 h-3 text-black" />
            </div>
            <span className="text-sm font-medium text-white">MediaKshetra GIOS</span>
            <span className="text-[10px]" style={{ color: GOLD_MUTED }}>™</span>
          </div>
          <p className="text-xs mb-2" style={{ color: MUTED }}>
            A Growth Infrastructure Operating System
          </p>
          <p className="text-xs" style={{ color: `${MUTED}80` }}>
            © {new Date().getFullYear()} MediaKshetra. All Rights Reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
